<script setup lang="ts">
import {ref, computed} from 'vue'
import { useUserStore } from '@/stores/user'
import { useGeoStore } from '@/stores/geo'

import {formatTime} from '@/utils/datetime'

import type {ReportTemplate} from '@/types'
import {Button} from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {NotepadTextDashed, Plus} from 'lucide-vue-next'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import LibraryTemplateDetail from '@/pages/Project/Templates/LibraryTemplateDetail.vue'

const userStore = useUserStore()

const geoStore = useGeoStore()
const settings = computed(() => userStore.settings)

const props = defineProps<{
  templates: ReportTemplate[],
  projectId: number
}>()

// Track which template is being added
const selectedTemplate = ref<ReportTemplate | null>(null)
const confirmDialogRef = ref<InstanceType<typeof DialogConfirm> | null>(null)

// Track which template is being viewed
const viewTemplate = ref<ReportTemplate | null>(null)
const viewDialogOpen = ref(false)

const onRowClick = (template: ReportTemplate) => {
  viewTemplate.value = template
  viewDialogOpen.value = true
}

const onAddClick = (template: ReportTemplate) => {
  selectedTemplate.value = template
  confirmDialogRef.value?.open()
}

const handleConfirmAttach = async () => {
  if (selectedTemplate.value) {
    await geoStore.attachTemplateToProject({
      projectId: props.projectId,
      template: selectedTemplate.value
    })
    selectedTemplate.value = null
  }
}

const handleCancelAttach = () => {
  selectedTemplate.value = null
}

const handleViewDialogClose = () => {
  viewDialogOpen.value = false
  viewTemplate.value = null
}
</script>

<template>
  <div class="rounded-md border">
    <table class="w-full caption-bottom text-sm">
      <thead class="[&_tr]:border-b">
        <tr class="border-b transition-colors hover:bg-muted/50">
          <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Icon</th>
          <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Name</th>
          <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Created</th>
          <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground"></th>
          <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground"></th>
        </tr>
      </thead>
      <tbody class="[&_tr:last-child]:border-0">
        <tr
          v-for="template in templates"
          :key="template.id"
          class="border-b transition-colors hover:bg-muted/50"
        >
          <td class="p-4 align-middle">
            <NotepadTextDashed v-if="!template.s3_icon" class="w-7 h-7 text-muted-foreground" />
            <img v-else :src="template.s3_icon" class="w-8 h-8 object-contain" />
          </td>
          <td class="p-4 align-middle">
            <div
              class="flex items-center gap-2 cursor-pointer"
              @click="onRowClick(template)"
              :data-testid="`library-template-name-${template.id}`"
            >
              <span class="text-foreground hover:text-primary">{{ template.name }}</span>
            </div>
          </td>
          <td class="p-4 align-middle text-muted-foreground whitespace-nowrap">
            {{ formatTime(template.created_at, settings.tz, 'D MMMM YYYY') }}
          </td>
          <td class="p-4 align-middle">
            <div class="w-4 h-4 rounded-full" :style="`background-color: ${template.color}`"></div>
          </td>
          <td class="p-4 align-middle">
            <Button
              variant="default"
              size="sm"
              @click.stop="onAddClick(template)"
              :data-testid="`add-template-${template.id}`"
            >
              <Plus class="w-4 h-4" />
            </Button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <DialogConfirm
    ref="confirmDialogRef"
    title="Add Template to Project"
    :description="`Are you sure you want to add '${selectedTemplate?.name}' to this project?`"
    confirm-text="Add"
    @confirm="handleConfirmAttach"
    @cancel="handleCancelAttach"
  />

  <!-- View-only dialog for template details -->
  <Dialog :open="viewDialogOpen" @update:open="(val: boolean) => { if (!val) handleViewDialogClose() }">
    <DialogContent class="max-w-md">
      <DialogHeader>
        <DialogTitle>Template Details</DialogTitle>
        <DialogDescription></DialogDescription>
      </DialogHeader>
      <LibraryTemplateDetail v-if="viewTemplate" :template="viewTemplate" />
      <DialogFooter>
        <Button variant="outline" @click="handleViewDialogClose">Close</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
